import React, { useCallback, useState, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { ArrowLeft, Play, HelpCircle, ClipboardList, Undo, Redo, Save, Upload, Plus, X, Zap, Pause, MessageSquare, Star, Tag, SkipForward, RefreshCw, Calendar, Gift, Phone, GitBranch, Clock, Shuffle, CheckCircle, XCircle, PauseCircle, CornerUpLeft, Smartphone, ChevronRight, Layout, ArrowRight, AlertCircle } from 'lucide-react';
interface CancellationFlowPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
// Node type definitions
interface NodeType {
  id: string;
  category: 'trigger' | 'collect' | 'offer' | 'logic' | 'action';
  icon: React.ReactNode;
  name: string;
  description: string;
  color: string;
}
const nodeTypes: NodeType[] = [
// Triggers
{
  id: 'cancel-request',
  category: 'trigger',
  icon: <Zap className="w-4 h-4" data-id="element-4850" />,
  name: 'Cancel Request',
  description: 'When customer initiates cancellation',
  color: '#8B5CF6'
}, {
  id: 'pause-request',
  category: 'trigger',
  icon: <Pause className="w-4 h-4" data-id="element-4851" />,
  name: 'Pause Request',
  description: 'When customer initiates pause',
  color: '#8B5CF6'
},
// Collect Information
{
  id: 'reason-selection',
  category: 'collect',
  icon: <MessageSquare className="w-4 h-4" data-id="element-4852" />,
  name: 'Reason Selection',
  description: "Ask why they're leaving",
  color: '#3B82F6'
}, {
  id: 'feedback-form',
  category: 'collect',
  icon: <MessageSquare className="w-4 h-4" data-id="element-4853" />,
  name: 'Feedback Form',
  description: 'Collect written feedback',
  color: '#3B82F6'
}, {
  id: 'survey',
  category: 'collect',
  icon: <Star className="w-4 h-4" data-id="element-4854" />,
  name: 'Survey',
  description: 'Multiple choice survey',
  color: '#3B82F6'
},
// Retention Offers
{
  id: 'offer-discount',
  category: 'offer',
  icon: <Tag className="w-4 h-4" data-id="element-4855" />,
  name: 'Offer Discount',
  description: 'Offer percentage or fixed discount',
  color: '#10B981'
}, {
  id: 'offer-skip',
  category: 'offer',
  icon: <SkipForward className="w-4 h-4" data-id="element-4856" />,
  name: 'Offer Skip',
  description: 'Suggest skipping next order',
  color: '#10B981'
}, {
  id: 'offer-swap',
  category: 'offer',
  icon: <RefreshCw className="w-4 h-4" data-id="element-4857" />,
  name: 'Offer Swap',
  description: 'Suggest product swap',
  color: '#10B981'
}, {
  id: 'offer-reschedule',
  category: 'offer',
  icon: <Calendar className="w-4 h-4" data-id="element-4858" />,
  name: 'Offer Reschedule',
  description: 'Suggest changing delivery date',
  color: '#10B981'
}, {
  id: 'offer-gift',
  category: 'offer',
  icon: <Gift className="w-4 h-4" data-id="element-4859" />,
  name: 'Offer Gift',
  description: 'Suggest gifting to someone else',
  color: '#10B981'
}, {
  id: 'offer-pause',
  category: 'offer',
  icon: <Pause className="w-4 h-4" data-id="element-4860" />,
  name: 'Offer Pause',
  description: 'Suggest pausing instead',
  color: '#10B981'
}, {
  id: 'contact-support',
  category: 'offer',
  icon: <Phone className="w-4 h-4" data-id="element-4861" />,
  name: 'Contact Support',
  description: 'Direct to support team',
  color: '#10B981'
},
// Logic
{
  id: 'condition',
  category: 'logic',
  icon: <GitBranch className="w-4 h-4" data-id="element-4862" />,
  name: 'Condition',
  description: 'Branch based on reason or response',
  color: '#F59E0B'
}, {
  id: 'delay',
  category: 'logic',
  icon: <Clock className="w-4 h-4" data-id="element-4863" />,
  name: 'Delay',
  description: 'Wait before next step',
  color: '#F59E0B'
}, {
  id: 'ab-test',
  category: 'logic',
  icon: <Shuffle className="w-4 h-4" data-id="element-4864" />,
  name: 'A/B Test',
  description: 'Split traffic for testing',
  color: '#F59E0B'
},
// Actions
{
  id: 'confirm-cancel',
  category: 'action',
  icon: <XCircle className="w-4 h-4" data-id="element-4865" />,
  name: 'Confirm Cancel',
  description: 'Proceed with cancellation',
  color: '#64748B'
}, {
  id: 'confirm-pause',
  category: 'action',
  icon: <PauseCircle className="w-4 h-4" data-id="element-4866" />,
  name: 'Confirm Pause',
  description: 'Proceed with pause',
  color: '#64748B'
}, {
  id: 'return-portal',
  category: 'action',
  icon: <CornerUpLeft className="w-4 h-4" data-id="element-4867" />,
  name: 'Return to Portal',
  description: 'Cancel the cancellation flow',
  color: '#64748B'
}];
export function CancellationFlowPage({
  currentPage = 'Settings',
  onNavigate
}: CancellationFlowPageProps) {
  const navigate = useNavigate();
  // State
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [flowStatus, setFlowStatus] = useState<'draft' | 'published'>('draft');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState('Cancellation Flow');
  const [showBanner, setShowBanner] = useState(true);
  // Navigation handler
  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      if (page === 'Dashboard') navigate('/');else if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else if (page === 'Contacts') navigate('/contacts');else if (page === 'Tools') navigate('/management/tools');else if (page === 'Cancellation Flow') navigate('/management/cancellation-flow');else navigate('/');
    }
  };
  // Group nodes by category
  const groupedNodes = {
    trigger: nodeTypes.filter(n => n.category === 'trigger'),
    collect: nodeTypes.filter(n => n.category === 'collect'),
    offer: nodeTypes.filter(n => n.category === 'offer'),
    logic: nodeTypes.filter(n => n.category === 'logic'),
    action: nodeTypes.filter(n => n.category === 'action')
  };
  const handleSaveDraft = () => {
    setHasUnsavedChanges(false);
    // Save logic here
  };
  const handlePublish = () => {
    setFlowStatus('published');
    setHasUnsavedChanges(false);
    // Publish logic here
  };
  // Check if mobile/tablet
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  if (isMobile) {
    return <div className="flex h-screen bg-[#F1F5F9] items-center justify-center p-6" data-id="element-4868">
        <div className="max-w-md text-center" data-id="element-4869">
          <Layout className="w-16 h-16 text-[#CBD5E1] mx-auto mb-4" data-id="element-4870" />
          <h2 className="text-xl font-semibold text-[#0F172A] mb-2" data-id="element-4871">
            Flow Builder Requires a Larger Screen
          </h2>
          <p className="text-[#64748B]" data-id="element-4872">
            The visual flow builder is optimized for desktop screens (1024px and
            wider). Please use a desktop or laptop computer to access this
            feature.
          </p>
        </div>
      </div>;
  }
  return <div className="flex flex-col overflow-hidden h-screen" data-id="element-4873">
      {/* Row 1: Page Header */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-4" data-id="element-4874">
        <div className="flex items-center justify-between" data-id="element-4875">
          <div className="flex items-center gap-4" data-id="element-4876">
            <button onClick={() => navigate('/management/tools')} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-4877">
              <ArrowLeft className="w-5 h-5 text-[#64748B]" data-id="element-4878" />
            </button>
            <h1 className="text-[24px] font-semibold text-[#0F172A]" data-id="element-4879">
              Cancellation Flow
            </h1>
          </div>
          <button className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" title="Help Documentation" data-id="element-4880">
            <HelpCircle className="w-5 h-5 text-[#64748B]" data-id="element-4881" />
          </button>
        </div>
      </div>

      {/* Row 2: Roadmap Banner */}
      {showBanner && <div className="bg-[#F8FAFC] border-b border-[#E2E8F0] px-6 py-3" data-id="element-4882">
          <div className="flex items-center justify-between gap-4" data-id="element-4883">
            <div className="flex items-center gap-3" data-id="element-4884">
              <span className="inline-flex items-center px-2 py-1 rounded text-[11px] font-semibold uppercase tracking-wide bg-white border border-[#8B5CF6] text-[#8B5CF6]" data-id="element-4885">
                COMING Q2 2026
              </span>
              <span className="text-[14px] text-[#64748B]" data-id="element-4886">
                Visual flow builder coming soon
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-4887">
              <button onClick={() => navigate('/management/tools/cancellation-settings')} className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] flex items-center gap-1 transition-colors" data-id="element-4888">
                Use Current Version
                <ArrowRight className="w-4 h-4" data-id="element-4889" />
              </button>
              <button onClick={() => setShowBanner(false)} className="p-1 hover:bg-[#E2E8F0] rounded transition-colors" data-id="element-4890">
                <X className="w-4 h-4 text-[#64748B]" data-id="element-4891" />
              </button>
            </div>
          </div>
        </div>}

      {/* Row 3: Canvas Toolbar */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-3" data-id="element-4892">
        <div className="flex items-center justify-between" data-id="element-4893">
          <div className="flex items-center gap-4" data-id="element-4894">
            <select value={selectedFlow} onChange={e => setSelectedFlow(e.target.value)} className="h-[36px] px-3 pr-8 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundPosition: 'right 8px center',
            backgroundSize: '14px 14px'
          }} data-id="element-4895">
              <option data-id="element-4896">Cancellation Flow</option>
              <option data-id="element-4897">Pause Flow</option>
              <option value="new" data-id="element-4898">+ New Flow</option>
            </select>
            <Badge variant="warning" data-id="element-4899">Draft</Badge>
          </div>
          <div className="flex items-center gap-3" data-id="element-4900">
            <div className="relative group" data-id="element-4901">
              <button className="p-2 rounded-md transition-colors opacity-40 cursor-not-allowed" title="Undo" disabled data-id="element-4902">
                <Undo className="w-4 h-4 text-[#64748B]" data-id="element-4903" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-4904">
                Available when flow builder launches
              </div>
            </div>
            <div className="relative group" data-id="element-4905">
              <button className="p-2 rounded-md transition-colors opacity-40 cursor-not-allowed" title="Redo" disabled data-id="element-4906">
                <Redo className="w-4 h-4 text-[#64748B]" data-id="element-4907" />
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-4908">
                Available when flow builder launches
              </div>
            </div>
            <div className="w-px h-6 bg-[#E2E8F0]" data-id="element-4909" />
            <div className="relative group" data-id="element-4910">
              <Button variant="secondary" size="sm" iconLeft={<Save className="w-4 h-4" data-id="element-4912" />} disabled className="opacity-40 cursor-not-allowed" data-id="element-4911">
                Save Draft
              </Button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-4913">
                Available when flow builder launches
              </div>
            </div>
            <div className="relative group" data-id="element-4914">
              <Button variant="primary" size="sm" iconLeft={<Upload className="w-4 h-4" data-id="element-4916" />} disabled className="opacity-40 cursor-not-allowed" data-id="element-4915">
                Publish
              </Button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-4917">
                Available when flow builder launches
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content: Three-panel layout */}
      <div className="flex-1 flex overflow-hidden" data-id="element-4918">
        {/* Left Sidebar: Node Palette */}
        <div className="w-[280px] bg-white border-r border-[#E2E8F0] overflow-y-auto" data-id="element-4919">
          <div className="p-4" data-id="element-4920">
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-4" data-id="element-4921">
              Flow Elements
            </h3>

            {/* Triggers */}
            <div className="mb-5" data-id="element-4922">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-4923">
                TRIGGERS
              </h4>
              <div className="space-y-2" data-id="element-4924">
                {groupedNodes.trigger.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#8B5CF6] hover:shadow-sm transition-all group" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-4925">
                    <div className="flex items-center gap-2" data-id="element-4926">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-4927">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4928">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-4929">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-4930">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Collect Information */}
            <div className="mb-5" data-id="element-4931">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-4932">
                COLLECT INFORMATION
              </h4>
              <div className="space-y-2" data-id="element-4933">
                {groupedNodes.collect.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#3B82F6] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-4934">
                    <div className="flex items-center gap-2" data-id="element-4935">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-4936">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4937">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-4938">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-4939">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Retention Offers */}
            <div className="mb-5" data-id="element-4940">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-4941">
                RETENTION OFFERS
              </h4>
              <div className="space-y-2" data-id="element-4942">
                {groupedNodes.offer.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#10B981] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-4943">
                    <div className="flex items-center gap-2" data-id="element-4944">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-4945">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4946">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-4947">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-4948">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Logic */}
            <div className="mb-5" data-id="element-4949">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-4950">
                LOGIC
              </h4>
              <div className="space-y-2" data-id="element-4951">
                {groupedNodes.logic.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#F59E0B] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-4952">
                    <div className="flex items-center gap-2" data-id="element-4953">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-4954">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4955">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-4956">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94A3B8] truncate" data-id="element-4957">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-[#E2E8F0] pt-5" data-id="element-4958">
              <h4 className="text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-4959">
                ACTIONS
              </h4>
              <div className="space-y-2" data-id="element-4960">
                {groupedNodes.action.map(node => <div key={node.id} draggable className="p-2.5 bg-white border border-[#E2E8F0] rounded-lg cursor-move hover:border-[#64748B] hover:shadow-sm transition-all" style={{
                borderLeftWidth: '3px',
                borderLeftColor: node.color
              }} data-id="element-4961">
                    <div className="flex items-center gap-2" data-id="element-4962">
                      <div className="p-1.5 rounded flex-shrink-0" style={{
                    backgroundColor: `${node.color}15`
                  }} data-id="element-4963">
                        {cloneElement(node.icon as React.ReactElement, {
                      style: {
                        color: node.color
                      }
                    })}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4964">
                        <div className="text-[13px] font-medium text-[#0F172A] truncate" data-id="element-4965">
                          {node.name}
                        </div>
                        <div className="text-[11px] text-[#94748B] truncate" data-id="element-4966">
                          {node.description}
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Center: Canvas with Static Demo Flow */}
        <div className="flex-1 relative bg-[#FAFBFC] overflow-auto" data-id="element-4967">
          {/* Static Demo Flow */}
          <div className="absolute inset-0 p-12" data-id="element-4968">
            <svg width="100%" height="100%" className="absolute inset-0" style={{
            zIndex: 0
          }} data-id="element-4969">
              {/* Connection lines */}
              {/* Start to Reason */}
              <path d="M 400 120 L 400 200" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-4970" />

              {/* Reason to branches */}
              <path d="M 400 280 Q 250 350 150 420" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="5,5" markerEnd="url(#arrowhead)" data-id="element-4971" />
              <path d="M 400 280 L 400 420" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="5,5" markerEnd="url(#arrowhead)" data-id="element-4972" />
              <path d="M 400 280 Q 550 350 650 420" stroke="#CBD5E1" strokeWidth="2" fill="none" strokeDasharray="5,5" markerEnd="url(#arrowhead)" data-id="element-4973" />

              {/* Branches to confirmation */}
              <path d="M 150 520 Q 250 590 400 620" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-4974" />
              <path d="M 400 520 L 400 620" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-4975" />
              <path d="M 650 520 Q 550 590 400 620" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-4976" />

              {/* Confirmation to outcomes */}
              <path d="M 400 700 Q 300 750 250 800" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-4977" />
              <path d="M 400 700 Q 500 750 550 800" stroke="#CBD5E1" strokeWidth="2" fill="none" markerEnd="url(#arrowhead)" data-id="element-4978" />

              <defs data-id="element-4979">
                <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" data-id="element-4980">
                  <polygon points="0 0, 10 3, 0 6" fill="#CBD5E1" data-id="element-4981" />
                </marker>
              </defs>
            </svg>

            {/* Flow Nodes */}
            <div className="relative" style={{
            zIndex: 1
          }} data-id="element-4982">
              {/* START: Cancel Request */}
              <div className="absolute" style={{
              left: '280px',
              top: '40px'
            }} data-id="element-4983">
                <div className="w-[240px] bg-white border-l-4 border-[#8B5CF6] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-4984">
                  <div className="flex items-center gap-3 mb-2" data-id="element-4985">
                    <div className="p-2 rounded bg-[#8B5CF615]" data-id="element-4986">
                      <Zap className="w-5 h-5 text-[#8B5CF6]" data-id="element-4987" />
                    </div>
                    <div className="flex-1" data-id="element-4988">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-4989">
                        Cancel Request
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-4990">
                        When customer initiates cancellation
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* NODE 2: Reason Selection */}
              <div className="absolute" style={{
              left: '280px',
              top: '180px'
            }} data-id="element-4991">
                <div className="w-[240px] bg-white border-l-4 border-[#3B82F6] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-4992">
                  <div className="flex items-center gap-3 mb-2" data-id="element-4993">
                    <div className="p-2 rounded bg-[#3B82F615]" data-id="element-4994">
                      <MessageSquare className="w-5 h-5 text-[#3B82F6]" data-id="element-4995" />
                    </div>
                    <div className="flex-1" data-id="element-4996">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-4997">
                        Reason Selection
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-4998">
                        Why are you leaving?
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-[#94A3B8] space-y-1" data-id="element-4999">
                    <div data-id="element-5000">• Too expensive</div>
                    <div data-id="element-5001">• Don't need it</div>
                    <div data-id="element-5002">• Other reasons</div>
                  </div>
                </div>
              </div>

              {/* Branch labels */}
              <div className="absolute" style={{
              left: '180px',
              top: '330px'
            }} data-id="element-5003">
                <span className="inline-block px-2 py-1 bg-white border border-[#E2E8F0] rounded text-[11px] text-[#64748B] shadow-sm" data-id="element-5004">
                  Too expensive
                </span>
              </div>
              <div className="absolute" style={{
              left: '370px',
              top: '330px'
            }} data-id="element-5005">
                <span className="inline-block px-2 py-1 bg-white border border-[#E2E8F0] rounded text-[11px] text-[#64748B] shadow-sm" data-id="element-5006">
                  Don't need it
                </span>
              </div>
              <div className="absolute" style={{
              left: '600px',
              top: '330px'
            }} data-id="element-5007">
                <span className="inline-block px-2 py-1 bg-white border border-[#E2E8F0] rounded text-[11px] text-[#64748B] shadow-sm" data-id="element-5008">
                  Other
                </span>
              </div>

              {/* BRANCH A: Offer Discount */}
              <div className="absolute" style={{
              left: '30px',
              top: '400px'
            }} data-id="element-5009">
                <div className="w-[240px] bg-white border-l-4 border-[#10B981] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-5010">
                  <div className="flex items-center gap-3 mb-2" data-id="element-5011">
                    <div className="p-2 rounded bg-[#10B98115]" data-id="element-5012">
                      <Tag className="w-5 h-5 text-[#10B981]" data-id="element-5013" />
                    </div>
                    <div className="flex-1" data-id="element-5014">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5015">
                        Offer Discount
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-5016">
                        20% off next 3 months
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* BRANCH B: Offer Pause */}
              <div className="absolute" style={{
              left: '280px',
              top: '400px'
            }} data-id="element-5017">
                <div className="w-[240px] bg-white border-l-4 border-[#F59E0B] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-5018">
                  <div className="flex items-center gap-3 mb-2" data-id="element-5019">
                    <div className="p-2 rounded bg-[#F59E0B15]" data-id="element-5020">
                      <Pause className="w-5 h-5 text-[#F59E0B]" data-id="element-5021" />
                    </div>
                    <div className="flex-1" data-id="element-5022">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5023">
                        Offer Pause
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-5024">
                        Pause for up to 3 months
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* BRANCH C: Feedback Form */}
              <div className="absolute" style={{
              left: '530px',
              top: '400px'
            }} data-id="element-5025">
                <div className="w-[240px] bg-white border-l-4 border-[#3B82F6] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-5026">
                  <div className="flex items-center gap-3 mb-2" data-id="element-5027">
                    <div className="p-2 rounded bg-[#3B82F615]" data-id="element-5028">
                      <MessageSquare className="w-5 h-5 text-[#3B82F6]" data-id="element-5029" />
                    </div>
                    <div className="flex-1" data-id="element-5030">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5031">
                        Feedback Form
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-5032">
                        Help us improve
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* NODE 4: Final Confirmation */}
              <div className="absolute" style={{
              left: '280px',
              top: '600px'
            }} data-id="element-5033">
                <div className="w-[240px] bg-white border-l-4 border-[#64748B] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-5034">
                  <div className="flex items-center gap-3 mb-2" data-id="element-5035">
                    <div className="p-2 rounded bg-[#64748B15]" data-id="element-5036">
                      <AlertCircle className="w-5 h-5 text-[#64748B]" data-id="element-5037" />
                    </div>
                    <div className="flex-1" data-id="element-5038">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5039">
                        Final Confirmation
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-5040">
                        Are you sure?
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* END NODE A: Cancelled */}
              <div className="absolute" style={{
              left: '130px',
              top: '780px'
            }} data-id="element-5041">
                <div className="w-[240px] bg-white border-l-4 border-[#EF4444] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-5042">
                  <div className="flex items-center gap-3 mb-2" data-id="element-5043">
                    <div className="p-2 rounded bg-[#EF444415]" data-id="element-5044">
                      <XCircle className="w-5 h-5 text-[#EF4444]" data-id="element-5045" />
                    </div>
                    <div className="flex-1" data-id="element-5046">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5047">
                        Cancelled
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-5048">
                        Subscription ended
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* END NODE B: Retained */}
              <div className="absolute" style={{
              left: '430px',
              top: '780px'
            }} data-id="element-5049">
                <div className="w-[240px] bg-white border-l-4 border-[#10B981] rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow cursor-pointer" data-id="element-5050">
                  <div className="flex items-center gap-3 mb-2" data-id="element-5051">
                    <div className="p-2 rounded bg-[#10B98115]" data-id="element-5052">
                      <CheckCircle className="w-5 h-5 text-[#10B981]" data-id="element-5053" />
                    </div>
                    <div className="flex-1" data-id="element-5054">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-5055">
                        Retained
                      </div>
                      <div className="text-[12px] text-[#64748B]" data-id="element-5056">
                        Customer saved
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Zoom controls */}
          <div className="absolute bottom-6 left-6 flex flex-col gap-2 bg-white border border-[#E2E8F0] rounded-lg shadow-sm overflow-hidden" data-id="element-5057">
            <button className="p-2 hover:bg-[#F1F5F9] transition-colors" data-id="element-5058">
              <Plus className="w-4 h-4 text-[#64748B]" data-id="element-5059" />
            </button>
            <div className="h-px bg-[#E2E8F0]" data-id="element-5060" />
            <button className="p-2 hover:bg-[#F1F5F9] transition-colors" data-id="element-5061">
              <X className="w-4 h-4 text-[#64748B]" data-id="element-5062" />
            </button>
          </div>

          {/* Minimap */}
          <div className="absolute bottom-6 right-6 w-48 h-32 bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-2" data-id="element-5063">
            <div className="w-full h-full bg-[#F8FAFC] rounded relative overflow-hidden" data-id="element-5064">
              {/* Miniature flow visualization */}
              <div className="absolute inset-2" data-id="element-5065">
                <div className="w-full h-full flex flex-col items-center justify-center gap-1" data-id="element-5066">
                  <div className="w-12 h-2 bg-[#8B5CF6] rounded-full opacity-50" data-id="element-5067"></div>
                  <div className="w-12 h-2 bg-[#3B82F6] rounded-full opacity-50" data-id="element-5068"></div>
                  <div className="flex gap-1" data-id="element-5069">
                    <div className="w-5 h-2 bg-[#10B981] rounded-full opacity-50" data-id="element-5070"></div>
                    <div className="w-5 h-2 bg-[#F59E0B] rounded-full opacity-50" data-id="element-5071"></div>
                    <div className="w-5 h-2 bg-[#3B82F6] rounded-full opacity-50" data-id="element-5072"></div>
                  </div>
                  <div className="w-12 h-2 bg-[#64748B] rounded-full opacity-50" data-id="element-5073"></div>
                  <div className="flex gap-1" data-id="element-5074">
                    <div className="w-5 h-2 bg-[#EF4444] rounded-full opacity-50" data-id="element-5075"></div>
                    <div className="w-5 h-2 bg-[#10B981] rounded-full opacity-50" data-id="element-5076"></div>
                  </div>
                </div>
                {/* Viewport indicator */}
                <div className="absolute inset-0 border-2 border-[#0EA5E9] rounded opacity-30" data-id="element-5077"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}